import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import { GetServerSidePropsContext } from 'next';

import { TimerDiscount, TimerDiscountOfferSourceEnum } from '~/api/book/discountBooksApiTypes';
import { isLoggedInSelector } from '~/feature/user/isLoggedInSelector';
import { getQueriesFromUrl } from '~/feature/utils/getQueriesFromUrl/getQueriesFromUrl';
import { activateCatalogTimerDiscountActions } from '~/feature/withTiwerDiscount/activateCatalogTimerDiscount/activateCatalogTimerDiscount.slice';
import {
  activateTimerDiscount,
  getActivateTimerDiscountTypeFromUrl,
} from '~/feature/withTiwerDiscount/withTimerDiscount.data';
import { withTimerDiscountSelector } from '~/feature/withTiwerDiscount/withTimerDiscount.selectors';
import { withTimerDiscountActions, withTimerDiscountSliceName } from '~/feature/withTiwerDiscount/withTimerDiscount.slice';
import { RootState } from '~/store';

export const activateCatalogTimerDiscountAfterLoginParam = 'withActivate';

export const activateCatalogTimerDiscount = createAsyncThunk<
TimerDiscount,
void,
{ state: RootState }
>(
  `${withTimerDiscountSliceName}/activateCatalogTimerDiscount`,
  async (_, thunkAPI) => {
    const isLoggedIn = isLoggedInSelector(thunkAPI.getState());
    const { activateTimerDiscountOfferSource } = withTimerDiscountSelector(thunkAPI.getState());

    try {
      if (!isLoggedIn) {
        thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsOpenAuthModalWithActivateCatalogTimerDiscount(true));
        return;
      }

      if (activateTimerDiscountOfferSource === TimerDiscountOfferSourceEnum.Night) {
        thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsShowActivateLibraryNightTimerDiscountLoadingModal(true));
      } else {
        thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsShowActivateCatalogTimerDiscountLoadingModal(true));
      }

      const result = unwrapResult(
        await thunkAPI.dispatch(activateTimerDiscount({
          offerSource: activateTimerDiscountOfferSource === TimerDiscountOfferSourceEnum.Night
            ? TimerDiscountOfferSourceEnum.Ad
            : activateTimerDiscountOfferSource,
        })),
      );

      thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsSuccessActivateCatalogTimerDiscount(true));
      thunkAPI.dispatch(withTimerDiscountActions.changeTimer(result));
    } catch (e) {
      return thunkAPI.rejectWithValue({ error: e.message });
    } finally {
      thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsShowActivateLibraryNightTimerDiscountLoadingModal(false));
      thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsShowActivateCatalogTimerDiscountLoadingModal(false));
    }
  },
);

export const checkActivateCatalogTimerDiscount = createAsyncThunk<
void,
GetServerSidePropsContext | void,
{ state: RootState }
>(
  `${withTimerDiscountSliceName}/checkActivateCatalogTimerDiscount`,
  async (
    ctx,
    thunkAPI,
  ) => {
    const offerSource = await getActivateTimerDiscountTypeFromUrl();
    thunkAPI.dispatch(withTimerDiscountActions.changeActivateTimerDiscountOfferSource(offerSource));

    if (offerSource === TimerDiscountOfferSourceEnum.Mail) {
      thunkAPI.dispatch(activateCatalogTimerDiscount());
    }

    if (offerSource === TimerDiscountOfferSourceEnum.Ad) {
      const queries = await getQueriesFromUrl({ searchedQueries: [activateCatalogTimerDiscountAfterLoginParam] });

      if (queries?.withActivate) {
        thunkAPI.dispatch(activateCatalogTimerDiscount());
      } else {
        await thunkAPI.dispatch(activateCatalogTimerDiscountActions.setIsShowActivateCatalogTimerDiscountModal(true));
      }
    }
  },
);

export const checkActivateLibraryNightTimerDiscount = createAsyncThunk<
void,
GetServerSidePropsContext | void,
{ state: RootState }
>(
  `${withTimerDiscountSliceName}/checkActivateLibraryNightTimerDiscount`,
  async (
    ctx,
    thunkAPI,
  ) => {
    const offerSource = await getActivateTimerDiscountTypeFromUrl();

    if (offerSource === TimerDiscountOfferSourceEnum.Night) {
      thunkAPI.dispatch(withTimerDiscountActions.changeActivateTimerDiscountOfferSource(TimerDiscountOfferSourceEnum.Night));

      thunkAPI.dispatch(activateCatalogTimerDiscount());
    }
  },
);
